import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { AppSettings, ArticleCatalog, InventaireSession } from '../../shared/models';

const SESSION_KEY = 'inventaire_session';
const TOKENS_KEY = 'auth_tokens';
const CATALOG_KEY_PREFIX = 'article_catalog_';
const SETTINGS_KEY = 'app_settings';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private initPromise: Promise<Storage> | null = null;

  constructor(private storage: Storage) {}

  private ensureInit(): Promise<Storage> {
    if (!this.initPromise) {
      this.initPromise = this.storage.create();
    }
    return this.initPromise;
  }

  async init(): Promise<void> {
    await this.ensureInit();
  }

  async saveSession(session: InventaireSession): Promise<void> {
    const s = await this.ensureInit();
    await s.set(SESSION_KEY, JSON.stringify(session));
  }

  async getSession(): Promise<InventaireSession | null> {
    const s = await this.ensureInit();
    const raw = await s.get(SESSION_KEY);
    if (!raw) return null;
    const session: InventaireSession = JSON.parse(raw);
    session.createdAt = new Date(session.createdAt);
    session.articles.forEach((a) => (a.scannedAt = new Date(a.scannedAt)));
    if (session.sentAt) session.sentAt = new Date(session.sentAt);
    return session;
  }

  async clearSession(): Promise<void> {
    const s = await this.ensureInit();
    await s.remove(SESSION_KEY);
  }

  async hasSession(): Promise<boolean> {
    const session = await this.getSession();
    return session !== null && !session.isSent;
  }

  async saveTokens(tokens: { access_token: string; refresh_token: string }): Promise<void> {
    const s = await this.ensureInit();
    await s.set(TOKENS_KEY, JSON.stringify(tokens));
  }

  async getTokens(): Promise<{ access_token: string; refresh_token: string } | null> {
    const s = await this.ensureInit();
    const raw = await s.get(TOKENS_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  async clearTokens(): Promise<void> {
    const s = await this.ensureInit();
    await s.remove(TOKENS_KEY);
  }

  async saveCatalog(idInventaire: number, catalog: ArticleCatalog): Promise<void> {
    const s = await this.ensureInit();
    await s.set(CATALOG_KEY_PREFIX + idInventaire, JSON.stringify(catalog));
  }

  async getCatalog(idInventaire: number): Promise<ArticleCatalog | null> {
    const s = await this.ensureInit();
    const raw = await s.get(CATALOG_KEY_PREFIX + idInventaire);
    return raw ? (JSON.parse(raw) as ArticleCatalog) : null;
  }

  async clearCatalog(idInventaire: number): Promise<void> {
    const s = await this.ensureInit();
    await s.remove(CATALOG_KEY_PREFIX + idInventaire);
  }

  async saveSettings(settings: AppSettings): Promise<void> {
    const s = await this.ensureInit();
    await s.set(SETTINGS_KEY, JSON.stringify(settings));
  }

  async getSettings(): Promise<Partial<AppSettings> | null> {
    const s = await this.ensureInit();
    const raw = await s.get(SETTINGS_KEY);
    return raw ? (JSON.parse(raw) as Partial<AppSettings>) : null;
  }
}
